<template>
  <v-dialog v-model="dialogVisible" max-width="880px" scrollable>
    <v-card class="wage-modal">
      <!-- 헤더 -->
      <div class="modal-header">
        <div class="header-info">
          <span class="emp-count" v-if="employees.length > 1">{{ currentIndex + 1 }}/{{ employees.length }}</span>
          <div class="emp-selector-wrapper">
            <button 
              class="nav-btn prev" 
              @click="navigateEmployee(-1)" 
              :disabled="currentIndex <= 0"
              v-if="employees.length > 1"
            >
              <v-icon size="18">mdi-chevron-left</v-icon>
            </button>
            <v-autocomplete
              v-model="selectedEmployeeValue"
              :items="employeeOptions"
              item-title="text"
              item-value="value"
              density="compact"
              variant="outlined"
              hide-details
              class="emp-select"
              @update:model-value="onEmployeeSelect"
            />
            <button 
              class="nav-btn next" 
              @click="navigateEmployee(1)" 
              :disabled="currentIndex >= employees.length - 1"
              v-if="employees.length > 1"
            >
              <v-icon size="18">mdi-chevron-right</v-icon>
            </button>
          </div>
          <span class="family-badge" v-if="payrollDetail?.isFamilyOnlyCaregiver">가족요양</span>
          <span class="emp-meta">만 {{ employeeAge }}세 · 근속 {{ workPeriodText }}</span>
          <button class="edit-start-date-btn" @click="openStartDateModal" title="입사일·퇴사일 변경">
            <v-icon size="14">mdi-calendar-edit</v-icon>
            입사일·퇴사일
          </button>
        </div>
        <div class="header-actions">
          <button class="help-btn" @click="showHelp = true" title="도움말">
            <v-icon size="18">mdi-help</v-icon>
          </button>
          <button class="close-btn" @click="close">×</button>
        </div>
      </div>

      <!-- 도움말 모달 -->
      <v-dialog v-model="showHelp" max-width="680px" class="help-dialog">
        <v-card class="help-card">
          <div class="help-header">
            <div class="help-title">
              <v-icon size="24">mdi-help-circle</v-icon>
              사용 방법 안내
            </div>
            <button class="help-close" @click="showHelp = false">×</button>
          </div>
          
          <div class="help-body">
            <!-- 다른 직원 보기 -->
            <div class="help-section">
              <div class="section-title">
                <div class="section-icon"><v-icon size="16">mdi-account-switch</v-icon></div>
                다른 직원 보는 방법
              </div>
              <div class="section-content">
                <div class="help-step">
                  <div class="step-num">1</div>
                  <div class="step-content">
                    화면 왼쪽 위의 <strong>◀ ▶ 화살표 버튼</strong>을 누르면 이전/다음 직원으로 넘어가요.
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-num">2</div>
                  <div class="step-content">
                    또는 <strong>이름이 적힌 칸</strong>을 누르면 직원 목록이 나와요. 원하는 직원 이름을 선택하세요.
                  </div>
                </div>
              </div>
            </div>

            <!-- 탭 사용법 -->
            <div class="help-section">
              <div class="section-title">
                <div class="section-icon"><v-icon size="16">mdi-tab</v-icon></div>
                화면 위쪽 메뉴(탭) 사용법
              </div>
              <div class="section-content">
                <div class="help-step">
                  <div class="step-label">급여정보</div>
                  <div class="step-content">
                    이 직원이 <strong>얼마를 받는지</strong>, <strong>얼마가 빠지는지</strong> 확인하는 화면이에요.
                    <div class="step-tip">💡 금액에 마우스를 올리면 어떻게 계산됐는지 나와요!</div>
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-label">급여설정</div>
                  <div class="step-content">
                    4대보험, 수당, 시급 등을 <strong>설정하고 변경</strong>하는 화면이에요.
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-label">근무달력</div>
                  <div class="step-content">
                    <strong>달력 모양</strong>으로 이 직원이 언제 일했는지 볼 수 있어요.
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-label">서비스내역</div>
                  <div class="step-content">
                    이 직원이 한 달 동안 <strong>어떤 서비스를 했는지</strong> 목록으로 볼 수 있어요.
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-label">주차별근무</div>
                  <div class="step-content">
                    <strong>1주차, 2주차...</strong> 이렇게 주 단위로 근무시간을 볼 수 있어요.
                  </div>
                </div>
              </div>
            </div>

            <!-- 급여설정 사용법 -->
            <div class="help-section">
              <div class="section-title">
                <div class="section-icon"><v-icon size="16">mdi-cog</v-icon></div>
                급여설정 화면 사용법
              </div>
              <div class="section-content">
                <div class="help-step">
                  <div class="step-num">1</div>
                  <div class="step-content">
                    <strong>체크박스(□)</strong>를 눌러서 적용할 항목을 선택하세요.<br>
                    체크(✓)하면 적용, 체크 해제하면 미적용이에요.
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-num">2</div>
                  <div class="step-content">
                    <strong>금액 입력칸</strong>에 숫자를 직접 입력할 수 있어요.<br>
                    (예: 장기근속수당에 50000 입력)
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-num">3</div>
                  <div class="step-content">
                    <strong>시급</strong>은 아래쪽 표에서 수급자별로 따로 입력할 수 있어요.<br>
                    비워두면 자동으로 최저시급이 적용돼요.
                  </div>
                </div>
                <div class="help-step highlight-step">
                  <div class="step-num important">!</div>
                  <div class="step-content important-text">
                    설정을 바꾸면 <strong>'저장' 버튼이 빨간색</strong>으로 변해요.<br>
                    <strong>반드시 '저장' 버튼을 눌러야</strong> 변경 내용이 저장돼요!
                  </div>
                </div>
              </div>
            </div>

            <!-- 버튼 사용법 -->
            <div class="help-section">
              <div class="section-title">
                <div class="section-icon"><v-icon size="16">mdi-gesture-tap-button</v-icon></div>
                버튼 사용법
              </div>
              <div class="section-content">
                <div class="help-step">
                  <div class="step-label blue">과거정보 가져오기</div>
                  <div class="step-content">
                    누르면 <strong>지난달 설정을 그대로 가져와요</strong>.<br>
                    가져온 후에 '저장' 버튼을 눌러야 적용돼요.
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-label gray">되돌리기</div>
                  <div class="step-content">
                    실수로 잘못 바꿨을 때, 누르면 <strong>바꾸기 전 상태로 돌아가요</strong>.
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-label dark">저장</div>
                  <div class="step-content">
                    설정을 바꾼 후 <strong>이 버튼을 꼭 눌러야 저장</strong>돼요.<br>
                    버튼이 빨간색이면 "아직 저장 안 됐어요!"라는 뜻이에요.
                  </div>
                </div>
              </div>
            </div>

            <!-- 입사일·퇴사일 변경 -->
            <div class="help-section">
              <div class="section-title">
                <div class="section-icon"><v-icon size="16">mdi-calendar-edit</v-icon></div>
                입사일·퇴사일 변경 방법
              </div>
              <div class="section-content">
                <div class="help-step">
                  <div class="step-num">1</div>
                  <div class="step-content">
                    화면 왼쪽 위의 <strong>'입사일·퇴사일' 버튼</strong>을 누르세요.
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-num">2</div>
                  <div class="step-content">
                    새 창에서 <strong>입사일</strong>을 변경하거나, <strong>'퇴사 처리'</strong> 체크 후 퇴사일을 선택하세요.
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-num">3</div>
                  <div class="step-content">
                    <strong>'변경' 버튼</strong>을 눌러 저장하세요.
                    <div class="step-tip">⚠️ 입사일/퇴사일 변경 시 근속연수와 연차수당 계산에 영향을 줍니다!</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 270분초과/8시간 휴게시간 -->
            <div class="help-section">
              <div class="section-title">
                <div class="section-icon"><v-icon size="16">mdi-clock-alert</v-icon></div>
                270분초과 수가 휴게시간 안내
              </div>
              <div class="section-content">
                <div class="help-step">
                  <div class="step-label">270분초과</div>
                  <div class="step-content">
                    270분초과 수가의 경우 <strong>30분 휴게시간이 자동 차감</strong>됩니다.<br>
                    (예: 5시간 → 4.5시간으로 계산)
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-label">8시간 이상</div>
                  <div class="step-content">
                    단, <strong>8시간 이상</strong>인 경우에는 휴게시간이 <strong>차감되지 않습니다</strong>.
                    <div class="step-tip">💡 수가시간 버튼을 클릭하면 수동으로 시간을 조정할 수 있어요!</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 본인부담금 설정 -->
            <div class="help-section">
              <div class="section-title">
                <div class="section-icon"><v-icon size="16">mdi-percent</v-icon></div>
                본인부담금 설정 방법 (가족요양만 해당)
              </div>
              <div class="section-content">
                <div class="help-step">
                  <div class="step-num">1</div>
                  <div class="step-content">
                    급여설정 탭에서 <strong>'본인부담금' 입력칸</strong>을 누르세요.
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-num">2</div>
                  <div class="step-content">
                    새 창이 뜨면 <strong>원하는 비율(%)을 선택</strong>하세요.
                  </div>
                </div>
                <div class="help-step">
                  <div class="step-num">3</div>
                  <div class="step-content">
                    선택 후 <strong>'저장' 버튼</strong>을 눌러 저장하세요.
                  </div>
                </div>
              </div>
            </div>

            <!-- 창 닫기 -->
            <div class="help-section">
              <div class="section-title">
                <div class="section-icon"><v-icon size="16">mdi-close-circle</v-icon></div>
                창 닫는 방법
              </div>
              <div class="section-content">
                <div class="help-step">
                  <div class="step-content">
                    오른쪽 위의 <strong>× 버튼</strong>을 누르면 이 창이 닫혀요.
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="help-footer">
            <button class="help-close-btn" @click="showHelp = false">닫기</button>
          </div>
        </v-card>
      </v-dialog>

      <!-- 입사일·퇴사일 변경 모달 -->
      <v-dialog v-model="showStartDateModal" max-width="400px" class="start-date-dialog">
        <v-card class="start-date-card">
          <div class="start-date-header">
            <div class="start-date-title">
              <v-icon size="20">mdi-calendar-edit</v-icon>
              입사일·퇴사일 변경
            </div>
            <button class="start-date-close" @click="showStartDateModal = false">×</button>
          </div>
          
          <div class="start-date-body">
            <div class="emp-info-row">
              <span class="label">직원명</span>
              <span class="value">{{ employee?.name || '-' }}</span>
            </div>
            
            <div class="date-section">
              <div class="date-section-title">
                <v-icon size="14">mdi-calendar-arrow-right</v-icon>
                입사일
              </div>
              <div class="emp-info-row">
                <span class="label">현재</span>
                <span class="value">{{ formatDisplayDate(employee?.workStartDate) || '미등록' }}</span>
              </div>
              <div class="date-input-row">
                <span class="label">변경</span>
                <input 
                  type="date" 
                  v-model="newStartDate" 
                  class="date-input"
                  :max="newEndDate || todayDate"
                />
              </div>
            </div>

            <div class="date-section">
              <div class="date-section-title">
                <v-icon size="14">mdi-calendar-arrow-left</v-icon>
                퇴사일
              </div>
              <div class="emp-info-row">
                <span class="label">현재</span>
                <span class="value">{{ formatDisplayDate(employee?.workEndDate) || '재직중' }}</span>
              </div>
              <div class="date-input-row">
                <span class="label">변경</span>
                <div class="date-input-wrapper">
                  <input 
                    type="date" 
                    v-model="newEndDate" 
                    class="date-input"
                    :min="newStartDate"
                    :max="todayDate"
                    :disabled="!hasEndDate"
                  />
                  <label class="end-date-toggle">
                    <input type="checkbox" v-model="hasEndDate" />
                    <span>퇴사 처리</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="warning-text">
              <v-icon size="14" color="#f59e0b">mdi-alert</v-icon>
              입사일/퇴사일 변경 시 근속연수와 연차수당 계산에 영향을 줍니다.
            </div>
          </div>
          
          <div class="start-date-footer">
            <button class="cancel-btn" @click="showStartDateModal = false">취소</button>
            <button class="save-btn" @click="saveStartDate" :disabled="!newStartDate || isSavingStartDate">
              {{ isSavingStartDate ? '저장 중...' : '변경' }}
            </button>
          </div>
        </v-card>
      </v-dialog>

      <!-- 탭 -->
      <div class="tabs">
        <button :class="{ active: activeTab === 'payroll' }" @click="activeTab = 'payroll'">급여정보</button>
        <button :class="{ active: activeTab === 'settings' }" @click="activeTab = 'settings'">급여설정</button>
        <button :class="{ active: activeTab === 'calendar' }" @click="activeTab = 'calendar'">근무달력</button>
        <button :class="{ active: activeTab === 'services' }" @click="activeTab = 'services'">서비스내역</button>
        <button :class="{ active: activeTab === 'weekly' }" @click="activeTab = 'weekly'">주차별근무</button>
      </div>

      <!-- 확정 상태 알림 (급여설정 탭에서만 표시) -->
      <div v-if="isConfirmed && activeTab === 'settings'" class="confirmed-notice-modal">
        <v-icon size="18" color="#f59e0b">mdi-lock</v-icon>
        <span class="notice-text">
          급여가 <strong>확정</strong>되어 수정할 수 없습니다. 
          수정하려면 <strong>확정 해제</strong> 후 진행해주세요.
        </span>
      </div>

      <!-- 컨텐츠 -->
      <div class="modal-body">
        <!-- 급여상세 -->
        <div v-if="activeTab === 'payroll' && payrollDetail" class="payroll-content">
          <div class="payroll-grid">
            <!-- 지급 -->
            <div class="payroll-section">
              <div class="section-head">지급</div>
              <table class="data-table">
                <tbody>
                  <template v-if="payrollDetail.isFamilyOnlyCaregiver">
                    <tr class="has-tooltip" :title="getPayrollTooltip('familyBasicTotal')">
                      <td>기본급 (주휴·연차 포함)</td>
                      <td class="num">{{ formatMoney(payrollDetail.basicTotal) }}</td>
                    </tr>
                  </template>
                  <template v-else>
                    <tr class="has-tooltip" :title="getPayrollTooltip('basicPay')">
                      <td>기본급</td>
                      <td class="num">{{ formatMoney(payrollDetail.basicPay) }}</td>
                    </tr>
                    <tr class="has-tooltip" :title="getPayrollTooltip('holidayPay')">
                      <td>주휴수당</td>
                      <td class="num">{{ formatMoney(payrollDetail.holidayPay) }}</td>
                    </tr>
                    <tr class="has-tooltip" :title="getPayrollTooltip('vacationPay')">
                      <td>연차수당</td>
                      <td class="num">{{ formatMoney(payrollDetail.vacationPay) }}</td>
                    </tr>
                    <tr v-if="payrollDetail.mealAllowance > 0" class="has-tooltip" :title="getPayrollTooltip('mealAllowance')">
                      <td>식대</td>
                      <td class="num">{{ formatMoney(payrollDetail.mealAllowance) }}</td>
                    </tr>
                  </template>
                  <tr v-if="payrollDetail.longServicePay > 0" class="has-tooltip" :title="getPayrollTooltip('longServicePay')">
                    <td>장기근속수당</td>
                    <td class="num">{{ formatMoney(payrollDetail.longServicePay) }}</td>
                  </tr>
                  <tr v-if="payrollDetail.etcAllowance > 0" class="has-tooltip" :title="getPayrollTooltip('etcAllowance')">
                    <td>기타수당</td>
                    <td class="num">{{ formatMoney(payrollDetail.etcAllowance) }}</td>
                  </tr>
                  <tr v-if="payrollDetail.bosuEduPay > 0" class="has-tooltip" :title="getPayrollTooltip('bosuEduPay')">
                    <td>보수교육비</td>
                    <td class="num">{{ formatMoney(payrollDetail.bosuEduPay) }}</td>
                  </tr>
                  <tr v-if="payrollDetail.severeCareAddition > 0" class="has-tooltip" :title="getPayrollTooltip('severeCareAddition')">
                    <td>중증수급자가산</td>
                    <td class="num">{{ formatMoney(payrollDetail.severeCareAddition) }}</td>
                  </tr>
                  <tr v-if="payrollDetail.holidayAllowance > 0 || (!settings?.applyHoliday && payrollDetail.calcInfo?.hasHolidayWork)" class="has-tooltip" :title="getPayrollTooltip('holidayAllowance')">
                    <td>
                      휴일수당
                      <span v-if="!settings?.applyHoliday && payrollDetail.calcInfo?.hasHolidayWork" class="badge warn">미적용</span>
                    </td>
                    <td class="num" :class="{ 'text-muted': !settings?.applyHoliday }">{{ formatMoney(payrollDetail.holidayAllowance) }}</td>
                  </tr>
                  <tr v-if="payrollDetail.overtimeAllowance > 0 || (!settings?.applyOvertime && payrollDetail.calcInfo?.hasOvertimeWork)" class="has-tooltip" :title="getPayrollTooltip('overtimeAllowance')">
                    <td>
                      연장수당
                      <span v-if="!settings?.applyOvertime && payrollDetail.calcInfo?.hasOvertimeWork" class="badge warn">미적용</span>
                    </td>
                    <td class="num" :class="{ 'text-muted': !settings?.applyOvertime }">{{ formatMoney(payrollDetail.overtimeAllowance) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="has-tooltip" :title="getPayrollTooltip('totalPay')">
                    <td>지급 합계</td>
                    <td class="num">{{ formatMoney(payrollDetail.totalPay) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <!-- 공제 -->
            <div class="payroll-section">
              <div class="section-head">공제</div>
              <table class="data-table">
                <tbody>
                  <tr :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="deductionTooltip('nationalPension')">
                    <td>국민연금</td>
                    <td class="num">{{ formatMoney(payrollDetail.employeeDeductions?.nationalPension || 0) }}</td>
                  </tr>
                  <tr :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="deductionTooltip('healthInsurance')">
                    <td>건강보험</td>
                    <td class="num">{{ formatMoney(payrollDetail.employeeDeductions?.healthInsurance || 0) }}</td>
                  </tr>
                  <tr :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="deductionTooltip('longTermCare')">
                    <td>장기요양</td>
                    <td class="num">{{ formatMoney(payrollDetail.employeeDeductions?.longTermCare || 0) }}</td>
                  </tr>
                  <tr :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="deductionTooltip('employmentInsurance')">
                    <td>고용보험</td>
                    <td class="num">{{ formatMoney(payrollDetail.employeeDeductions?.employmentInsurance || 0) }}</td>
                  </tr>
                  <tr :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="deductionTooltip('incomeTax')">
                    <td>소득세</td>
                    <td class="num">{{ formatMoney(payrollDetail.employeeDeductions?.incomeTax || 0) }}</td>
                  </tr>
                  <tr :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="deductionTooltip('localTax')">
                    <td>지방세</td>
                    <td class="num">{{ formatMoney(payrollDetail.employeeDeductions?.localTax || 0) }}</td>
                  </tr>
                  <tr v-if="payrollDetail.boninPayAmount > 0" :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="deductionTooltip('boninPay')">
                    <td>본인부담금</td>
                    <td class="num">{{ formatMoney(payrollDetail.boninPayAmount) }}</td>
                  </tr>
                  <tr v-if="payrollDetail.empEtcAmount > 0" :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="deductionTooltip('empEtc')">
                    <td>기타공제</td>
                    <td class="num">{{ formatMoney(payrollDetail.empEtcAmount) }}</td>
                  </tr>
                  <tr v-if="payrollDetail.supportDeduction > 0" :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="deductionTooltip('supportDeduction')">
                    <td>지원공제</td>
                    <td class="num">{{ formatMoney(payrollDetail.supportDeduction) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="deductionTooltip('totalDeduction')">
                    <td>공제 합계</td>
                    <td class="num">{{ formatMoney(payrollDetail.totalEmployeeDeduction) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- 실수령액 -->
          <div class="net-pay-row has-tooltip" :title="getPayrollTooltip('netPay')">
            <span class="calc">{{ formatMoney(payrollDetail.totalPay) }} - {{ formatMoney(payrollDetail.totalEmployeeDeduction) }} =</span>
            <span class="label">실수령액</span>
            <span class="amount">{{ formatMoney(payrollDetail.netPay) }}원</span>
          </div>

          <!-- 부가정보 -->
          <div class="info-row">
            <div class="info-block">
              <div class="block-title">기관부담</div>
              <div class="block-item" :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="centerTooltip('insurance4')"><span>4대보험</span><span>{{ formatMoney((payrollDetail.centerDeductions?.nationalPension || 0) + (payrollDetail.centerDeductions?.healthInsurance || 0) + (payrollDetail.centerDeductions?.longTermCare || 0) + (payrollDetail.centerDeductions?.employmentInsurance || 0)) }}원</span></div>
              <div class="block-item" :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="centerTooltip('unemployment')"><span>실업급여</span><span>{{ formatMoney(payrollDetail.centerDeductions?.unemployment || 0) }}원</span></div>
              <div class="block-item" :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="centerTooltip('industrialAccident')"><span>산재보험</span><span>{{ formatMoney(payrollDetail.centerDeductions?.industrialAccident || 0) }}원</span></div>
              <div class="block-item" :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="centerTooltip('retirement')"><span>퇴직금</span><span>{{ formatMoney(payrollDetail.centerDeductions?.retirement || 0) }}원</span></div>
              <div class="block-item total" :class="{ 'has-tooltip': showAdminDeductionTooltip }" :title="centerTooltip('totalCenter')"><span>합계</span><span>{{ formatMoney(payrollDetail.totalCenterDeduction) }}원</span></div>
            </div>
            <div class="info-block">
              <div class="block-title">과세구분</div>
              <div class="block-item has-tooltip" :title="getTaxTooltip('taxable')"><span>과세금액</span><span>{{ formatMoney(payrollDetail.taxableAmount) }}원</span></div>
              <div class="block-item has-tooltip" :title="getTaxTooltip('nonTaxable')"><span>비과세금액</span><span>{{ formatMoney(payrollDetail.nonTaxableAmount) }}원</span></div>
            </div>
            <div class="info-block">
              <div class="block-title">기관수익</div>
              <div class="block-item profit has-tooltip" :class="{ positive: centerProfit > 0, negative: centerProfit < 0 }" :title="getProfitTooltip()"><span>수익</span><span>{{ formatMoney(centerProfit) }}원</span></div>
              <div class="block-item has-tooltip" :title="getRetirementTooltip()"><span>누적퇴직금</span><span>{{ formatMoney(cumulativeRetirement) }}원</span></div>
            </div>
          </div>

          <!-- 직원정보 (급여정보 탭 내 통합) -->
          <div class="employee-info-section">
            <div class="section-title">직원정보</div>
            <div class="info-grid">
              <div class="info-section">
                <div class="section-head">일반정보</div>
                <div class="info-list">
                  <div class="info-item" v-for="(item, idx) in employeeInfoItems" :key="idx">
                    <v-icon :size="14" :color="item.color">{{ item.icon }}</v-icon>
                    <span>{{ item.text }}</span>
                  </div>
                </div>
              </div>
              <div class="info-section">
                <div class="section-head">제한사항</div>
                <div class="info-list">
                  <template v-if="restrictionItems.length > 0">
                    <div class="info-item warn" v-for="(item, idx) in restrictionItems" :key="idx">
                      <v-icon :size="14" :color="item.color">{{ item.icon }}</v-icon>
                      <span>{{ item.text }}</span>
                    </div>
                  </template>
                  <div v-else class="info-item ok">
                    <v-icon size="14" color="#059669">mdi-check-circle</v-icon>
                    <span>제한사항 없음</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 서비스내역 -->
        <div v-if="activeTab === 'services'" class="services-content">
          <table class="data-table full sortable" v-if="services.length > 0">
            <thead>
              <tr>
                <th class="sortable-th" @click="toggleServiceSort('date')">
                  서비스일
                  <span class="sort-icon" :class="{ active: serviceSortKey === 'date' }">
                    {{ serviceSortKey === 'date' ? (serviceSortAsc ? '▲' : '▼') : '⇅' }}
                  </span>
                </th>
                <th class="sortable-th" @click="toggleServiceSort('patient')">
                  수급자
                  <span class="sort-icon" :class="{ active: serviceSortKey === 'patient' }">
                    {{ serviceSortKey === 'patient' ? (serviceSortAsc ? '▲' : '▼') : '⇅' }}
                  </span>
                </th>
                <th class="sortable-th" @click="toggleServiceSort('time')">
                  시간
                  <span class="sort-icon" :class="{ active: serviceSortKey === 'time' }">
                    {{ serviceSortKey === 'time' ? (serviceSortAsc ? '▲' : '▼') : '⇅' }}
                  </span>
                </th>
                <th class="sortable-th" @click="toggleServiceSort('suga')">
                  수가명
                  <span class="sort-icon" :class="{ active: serviceSortKey === 'suga' }">
                    {{ serviceSortKey === 'suga' ? (serviceSortAsc ? '▲' : '▼') : '⇅' }}
                  </span>
                </th>
                <th class="sortable-th" @click="toggleServiceSort('minutes')">
                  서비스시간
                  <span class="sort-icon" :class="{ active: serviceSortKey === 'minutes' }">
                    {{ serviceSortKey === 'minutes' ? (serviceSortAsc ? '▲' : '▼') : '⇅' }}
                  </span>
                </th>
                <th class="sortable-th r" @click="toggleServiceSort('money')">
                  수가금액
                  <span class="sort-icon" :class="{ active: serviceSortKey === 'money' }">
                    {{ serviceSortKey === 'money' ? (serviceSortAsc ? '▲' : '▼') : '⇅' }}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(s, idx) in sortedServices" :key="idx">
                <td>{{ s.service_date }}<span v-if="isHoliday(s.service_date)" class="badge red">휴</span></td>
                <td>{{ s.patient_name }}</td>
                <td>{{ s.start_time }}~{{ s.end_time }}<span v-if="s.is_lunch_time" class="badge blue">식대</span></td>
                <td :class="{ family: s.suga_name?.includes('가족') }">{{ s.suga_name }}</td>
                <td>{{ formatMinutes(s.service_minutes) }}</td>
                <td class="r">{{ formatMoney(s.suga_money || 0) }}원</td>
              </tr>
            </tbody>
          </table>
          <div v-else class="empty">서비스 내역이 없습니다.</div>

          <div v-if="severeCareDaily.length > 0" class="sub-section">
            <div class="sub-title">중증수급자 가산 <span class="sub-total">합계: {{ formatMoney(severeCareTotal) }}원 ({{ severeCareDaily.length }}회)</span></div>
            <table class="data-table full">
              <thead><tr><th>일자</th><th>수급자</th><th>수가명</th><th class="r">금액</th></tr></thead>
              <tbody>
                <tr v-for="(item, idx) in severeCareDaily" :key="idx">
                  <td>{{ formatServiceDate(item.service_date) }}</td>
                  <td>{{ item.patient_name || '-' }}</td>
                  <td>{{ item.suga_name }}</td>
                  <td class="r">{{ formatMoney(item.suga_money || 0) }}원</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 주차별근무 -->
        <div v-if="activeTab === 'weekly'" class="weekly-content">
          <table class="data-table full" v-if="weeklyServices.length > 0">
            <thead><tr><th>주차</th><th>서비스일</th><th>일일시간</th><th>주간합계</th></tr></thead>
            <tbody>
              <template v-for="(week, wIdx) in weeklyServices" :key="wIdx">
                <template v-for="(daily, dIdx) in week.daily_items" :key="`${wIdx}-${dIdx}`">
                  <tr :class="{ 'week-start': dIdx === 0 }">
                    <td v-if="dIdx === 0" :rowspan="week.daily_items.length" class="week-cell">{{ week.service_week }}주차</td>
                    <td>{{ daily.service_date }}<span v-if="isHoliday(daily.service_date)" class="badge red">휴</span></td>
                    <td :class="{ over: daily.daily_total_hours > 8 }">{{ daily.daily_total_hours }}시간</td>
                    <td v-if="dIdx === 0" :rowspan="week.daily_items.length" class="week-cell" :class="{ over: week.weekly_total_hours > 40 }">{{ week.weekly_total_hours }}시간</td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
          <div v-else class="empty">주차별 근무내역이 없습니다.</div>
        </div>

        <!-- 근무달력 -->
        <div v-if="activeTab === 'calendar'" class="calendar-content">
          <WageEmployeeCalendar :year-month="yearMonth || ''" :services="calendarServices" :holidays="holidays" />
        </div>

        <!-- 설정 -->
        <div v-if="activeTab === 'settings'" class="settings-content">
          <div class="settings-grid">
            <div class="settings-section">
              <div class="section-head">4대보험</div>
              <label>
                <input type="checkbox" :checked="settings?.applyNationalPension" @change="updateSetting('applyNationalPension', ($event.target as HTMLInputElement).checked)" :disabled="isConfirmed" />
                국민연금
                <input v-if="settings?.applyNationalPension" type="text" class="inline-input" :value="settings?.yeonPay" @input="updateSetting('yeonPay', ($event.target as HTMLInputElement).value)" :disabled="isConfirmed" />
              </label>
              <label>
                <input type="checkbox" :checked="settings?.applyHealthInsurance" @change="updateSetting('applyHealthInsurance', ($event.target as HTMLInputElement).checked)" :disabled="isConfirmed" />
                건강보험
              </label>
              <div class="checkbox-with-action">
                <label>
                  <input 
                    type="checkbox" 
                    :checked="settings?.applyEmploymentInsurance" 
                    @click.prevent="$emit('open-employment-modal', groupIndex)"
                    :disabled="isConfirmed" 
                  />
                  고용보험
                </label>
                <span v-if="!settings?.applyEmploymentInsurance && settings?.applyUnemployment" class="badge-inline">실업급여만</span>
              </div>
              <label>
                <input type="checkbox" :checked="settings?.applyRetirement" @change="updateSetting('applyRetirement', ($event.target as HTMLInputElement).checked)" :disabled="isConfirmed" />
                퇴직금 적립
              </label>
            </div>
            <div class="settings-section">
              <div class="section-head">수당</div>
              <label>
                <input type="checkbox" :checked="settings?.applyHoliday" @change="updateSetting('applyHoliday', ($event.target as HTMLInputElement).checked)" :disabled="isConfirmed" />
                휴일수당
              </label>
              <label>
                <input type="checkbox" :checked="settings?.applyOvertime" @change="updateSetting('applyOvertime', ($event.target as HTMLInputElement).checked)" :disabled="isConfirmed" />
                연장수당
              </label>
              <label>
                <input type="checkbox" :checked="settings?.applyMeal" @change="updateSetting('applyMeal', ($event.target as HTMLInputElement).checked)" :disabled="isConfirmed" />
                식대
              </label>
              <div class="input-row inline">
                <span>기타수당</span>
                <input type="text" :value="settings?.etcAllowance" @input="handleEtcAllowanceInput($event)" @blur="handleEtcAllowanceBlur($event)" :disabled="isConfirmed" placeholder="0" />
              </div>
            </div>
            <div class="settings-section">
              <div class="section-head">추가항목</div>
              <div class="input-row">
                <span>장기근속수당</span>
                <input type="text" :value="settings?.longServicePay" @input="handleLongServicePayInput($event)" @blur="handleLongServicePayBlur($event)" :disabled="isConfirmed" />
              </div>
              <div class="input-row">
                <span>보수교육비</span>
                <input type="text" :value="settings?.bosuEduPay" @input="handleBosuEduPayInput($event)" @blur="handleBosuEduPayBlur($event)" :disabled="isConfirmed" />
              </div>
              <div class="input-row">
                <span>본인부담금</span>
                <input 
                  type="text" 
                  :value="settings?.boninPay" 
                  readonly 
                  :class="{ 'clickable': hasFamilySuga && !isConfirmed, 'not-applicable': !hasFamilySuga }"
                  @click="handleBoninPayClick"
                  :disabled="isConfirmed"
                  :placeholder="hasFamilySuga ? '클릭하여 비율 선택' : '해당없음'"
                />
              </div>
              <div class="input-row">
                <span>기타공제</span>
                <input type="text" :value="settings?.empEtc" @input="handleEmpEtcInput($event)" @blur="handleEmpEtcBlur($event)" :disabled="isConfirmed" />
              </div>
            </div>
          </div>
          
          <!-- 수급자별 시급/수가시간 -->
          <div class="timepay-section" v-if="employee?.patients && employee.patients.length > 0">
            <div class="section-head">수급자별 시급 및 수가시간</div>
            <table class="data-table full">
              <thead><tr><th>수급자</th><th>수가명</th><th>서비스횟수</th><th>수가시간</th><th>시급</th><th class="r">합계금액</th></tr></thead>
              <tbody>
                <tr v-for="(p, pIdx) in employee.patients" :key="pIdx">
                  <td>{{ p.patientName }}</td>
                  <td :class="{ family: p.sugaName?.includes('가족'), holiday: isHolidaySuga(p.sugaName) }">
                    {{ p.sugaName }}
                    <span v-if="isHolidaySuga(p.sugaName)" class="badge holiday-badge">휴일수당</span>
                  </td>
                  <td class="service-count">{{ p.serviceDays || 0 }}회</td>
                  <td>
                    <button 
                      class="suga-hours-btn" 
                      @click="$emit('open-suga-hours-modal', groupIndex, p)"
                      :disabled="isConfirmed"
                    >
                      {{ getDisplaySugaHours(p) }}시간
                    </button>
                  </td>
                  <td>
                    <input 
                      type="text" 
                      class="timepay-input" 
                      :value="getTimePay(p)" 
                      @input="updateTimePay(p, ($event.target as HTMLInputElement).value)" 
                      :disabled="isConfirmed" 
                    />
                  </td>
                  <td class="r total-amount">
                    {{ formatMoney(getPatientTotalAmount(p)) }}원
                    <div v-if="isHolidaySuga(p.sugaName) && settings?.applyHoliday && getPatientHolidayAllowance(p) > 0" class="holiday-allowance-info">
                      (휴일 +{{ formatMoney(getPatientHolidayAllowance(p)) }}원 포함)
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="5" class="r">합계</td>
                  <td class="r total-amount">
                    {{ formatMoney(getAllPatientsTotalAmount()) }}원
                    <div v-if="getTotalHolidayAllowance() > 0 && settings?.applyHoliday" class="holiday-allowance-info">
                      (휴일 +{{ formatMoney(getTotalHolidayAllowance()) }}원 포함)
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- 저장/되돌리기 버튼 -->
          <div class="settings-actions">
            <button class="action-btn load-prev" @click="$emit('load-previous-settings', groupIndex)" :disabled="isConfirmed">
              과거정보 가져오기
            </button>
            <button class="action-btn reset" @click="$emit('reset-settings', groupIndex)" :disabled="isConfirmed || !hasChanges">
              되돌리기
            </button>
            <button class="action-btn save" :class="{ unsaved: hasChanges }" @click="$emit('save-settings')" :disabled="isConfirmed || isSaving">
              <span v-if="isSaving">저장 중...</span>
              <span v-else>{{ hasChanges ? '저장필요' : '저장' }}</span>
            </button>
          </div>

          <!-- 직원정보 (급여설정 탭 내) -->
          <div class="employee-info-section">
            <div class="section-title">직원정보</div>
            <div class="info-grid">
              <div class="info-section">
                <div class="section-head">일반정보</div>
                <div class="info-list">
                  <div class="info-item" v-for="(item, idx) in employeeInfoItems" :key="idx">
                    <v-icon :size="14" :color="item.color">{{ item.icon }}</v-icon>
                    <span>{{ item.text }}</span>
                  </div>
                </div>
              </div>
              <div class="info-section">
                <div class="section-head">제한사항</div>
                <div class="info-list">
                  <template v-if="restrictionItems.length > 0">
                    <div class="info-item warn" v-for="(item, idx) in restrictionItems" :key="idx">
                      <v-icon :size="14" :color="item.color">{{ item.icon }}</v-icon>
                      <span>{{ item.text }}</span>
                    </div>
                  </template>
                  <div v-else class="info-item ok">
                    <v-icon size="14" color="#059669">mdi-check-circle</v-icon>
                    <span>제한사항 없음</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import WageEmployeeCalendar from './WageEmployeeCalendar.vue';
import {
  shouldShowAdminDeductionTooltip,
  buildDeductionTooltip,
  buildCenterTooltip,
} from './payrollTooltipUtils';

const store = useStore();

interface Patient {
  patientCode: string;
  patientName: string;
  sugaCode: string;
  sugaName: string;
  sugaHours: number;
  sugaMinutesKey?: string | number | null;
  serviceDays: number;
  timePay?: string;
}

interface Employee {
  empCode: string;
  name: string;
  birth: string;
  workStartDate?: string;
  workEndDate?: string;
  patients: Patient[];
  totalSugaMoney: number;
  totalLunchTimeCount?: number;
}

interface Props {
  modelValue: boolean;
  employee: Employee | null;
  employees: Employee[];
  groupIndex: number;
  yearMonth?: string;
  sisulCode?: string;
  payrollDetail: any;
  settings: any;
  services: any[];
  weeklyServices: any[];
  severeCareItems: any[];
  severeCareDaily: any[];
  holidays: Record<string, string>;
  isConfirmed: boolean;
  centerProfit: number;
  cumulativeRetirement: number;
  retirementDetails?: { lemi_ym: string; retirement_amount: number }[];
  employeeInfoItems: { icon: string; color: string; text: string }[];
  restrictionItems: { icon: string; color: string; text: string }[];
  hasChanges?: boolean;
  isSaving?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  weeklyServices: () => [],
  severeCareItems: () => [],
  severeCareDaily: () => [],
  centerProfit: 0,
  cumulativeRetirement: 0,
  retirementDetails: () => [],
  employeeInfoItems: () => [],
  restrictionItems: () => [],
  hasChanges: false,
  isSaving: false
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'update:groupIndex', value: number): void;
  (e: 'update-setting', groupIndex: number, key: string, value: any): void;
  (e: 'update-timepay', groupIndex: number, patientCode: string, sugaKey: string, value: string): void;
  (e: 'open-bonin-modal', groupIndex: number): void;
  (e: 'open-employment-modal', groupIndex: number): void;
  (e: 'open-suga-hours-modal', groupIndex: number, patient: Patient): void;
  (e: 'reset-settings', groupIndex: number): void;
  (e: 'save-settings'): void;
  (e: 'load-previous-settings', groupIndex: number): void;
  (e: 'start-date-updated', groupIndex: number, startDate: string, endDate: string | null): void;
  (e: 'start-date-updated', groupIndex: number, newDate: string): void;
}>();

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const currentIndex = computed({
  get: () => props.groupIndex,
  set: (value) => emit('update:groupIndex', value)
});

// 사원 선택 드롭다운용
const employeeOptions = computed(() => {
  return props.employees.map((emp, idx) => ({
    value: idx,
    text: emp.name || '-'
  }));
});

const selectedEmployeeValue = computed({
  get: () => currentIndex.value,
  set: (value) => {
    if (value !== null && value !== undefined) {
      currentIndex.value = value;
    }
  }
});

const onEmployeeSelect = (value: number | null) => {
  if (value !== null && value !== undefined && value !== currentIndex.value) {
    currentIndex.value = value;
  }
};

const activeTab = ref<'payroll' | 'settings' | 'calendar' | 'services' | 'weekly'>('payroll');
const showHelp = ref(false);

// 입사일·퇴사일 변경 모달
const showStartDateModal = ref(false);
const newStartDate = ref('');
const newEndDate = ref('');
const hasEndDate = ref(false);
const isSavingStartDate = ref(false);

const todayDate = computed(() => {
  const today = new Date();
  return today.toISOString().split('T')[0];
});

const formatDisplayDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '';
  const cleaned = dateStr.replace(/[^0-9]/g, '');
  if (cleaned.length >= 8) {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 6)}-${cleaned.slice(6, 8)}`;
  } else if (cleaned.length >= 6) {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 6)}`;
  }
  return dateStr;
};

const openStartDateModal = () => {
  // 현재 입사일을 기본값으로 설정
  if (props.employee?.workStartDate) {
    const cleaned = props.employee.workStartDate.replace(/[^0-9]/g, '');
    if (cleaned.length >= 8) {
      newStartDate.value = `${cleaned.slice(0, 4)}-${cleaned.slice(4, 6)}-${cleaned.slice(6, 8)}`;
    }
  } else {
    newStartDate.value = '';
  }
  
  // 현재 퇴사일을 기본값으로 설정
  if (props.employee?.workEndDate) {
    const cleaned = props.employee.workEndDate.replace(/[^0-9]/g, '');
    if (cleaned.length >= 8) {
      newEndDate.value = `${cleaned.slice(0, 4)}-${cleaned.slice(4, 6)}-${cleaned.slice(6, 8)}`;
      hasEndDate.value = true;
    } else {
      newEndDate.value = '';
      hasEndDate.value = false;
    }
  } else {
    newEndDate.value = '';
    hasEndDate.value = false;
  }
  
  showStartDateModal.value = true;
};

const saveStartDate = async () => {
  if (!newStartDate.value || !props.employee) return;
  
  // props에서 sisulCode를 받거나, store에서 가져옴
  const sisulCode = props.sisulCode || store.state.user?.m_sisul_code;
  if (!sisulCode) {
    store.commit('showSnackbar', { text: '시설 정보를 찾을 수 없습니다.', color: 'error' });
    return;
  }
  
  isSavingStartDate.value = true;
  
  try {
    const response = await fetch('/api/emp/update-start-date', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        m_sisul_code: sisulCode,
        emp_name: props.employee.name,
        emp_birth: props.employee.birth,
        emp_in_date: newStartDate.value,
        emp_out_date: hasEndDate.value ? newEndDate.value : null
      })
    });
    
    const result = await response.json();
    
    if (result.success) {
      const message = hasEndDate.value 
        ? '입사일/퇴사일이 변경되었습니다.' 
        : '입사일이 변경되었습니다.';
      store.commit('showSnackbar', { text: message, color: 'success' });
      showStartDateModal.value = false;
      // 부모 컴포넌트에 입사일/퇴사일 변경 알림
      emit('start-date-updated', props.groupIndex, newStartDate.value, hasEndDate.value ? newEndDate.value : null);
    } else {
      store.commit('showSnackbar', { text: result.message || '변경에 실패했습니다.', color: 'error' });
    }
  } catch (error) {
    console.error('입사일/퇴사일 변경 오류:', error);
    store.commit('showSnackbar', { text: '변경 중 오류가 발생했습니다.', color: 'error' });
  } finally {
    isSavingStartDate.value = false;
  }
};

// 서비스내역 정렬
type ServiceSortKey = 'date' | 'patient' | 'time' | 'suga' | 'minutes' | 'money';
const serviceSortKey = ref<ServiceSortKey>('date');
const serviceSortAsc = ref(true);

const toggleServiceSort = (key: ServiceSortKey) => {
  if (serviceSortKey.value === key) {
    serviceSortAsc.value = !serviceSortAsc.value;
  } else {
    serviceSortKey.value = key;
    serviceSortAsc.value = true;
  }
};

const sortedServices = computed(() => {
  if (!props.services || props.services.length === 0) return [];
  
  const sorted = [...props.services].sort((a, b) => {
    let compareA: any, compareB: any;
    
    switch (serviceSortKey.value) {
      case 'date':
        compareA = a.service_date || '';
        compareB = b.service_date || '';
        break;
      case 'patient':
        compareA = a.patient_name || '';
        compareB = b.patient_name || '';
        break;
      case 'time':
        compareA = a.start_time || '';
        compareB = b.start_time || '';
        break;
      case 'suga':
        compareA = a.suga_name || '';
        compareB = b.suga_name || '';
        break;
      case 'minutes':
        compareA = a.service_minutes || 0;
        compareB = b.service_minutes || 0;
        break;
      case 'money':
        compareA = a.suga_money || 0;
        compareB = b.suga_money || 0;
        break;
      default:
        return 0;
    }
    
    if (compareA < compareB) return serviceSortAsc.value ? -1 : 1;
    if (compareA > compareB) return serviceSortAsc.value ? 1 : -1;
    return 0;
  });
  
  return sorted;
});

const navigateEmployee = (delta: number) => {
  const newIndex = currentIndex.value + delta;
  if (newIndex >= 0 && newIndex < props.employees.length) {
    currentIndex.value = newIndex;
  }
};

const employeeAge = computed(() => {
  if (!props.employee?.birth || !props.yearMonth) return 0;
  const birthStr = props.employee.birth.replace(/[^0-9]/g, '');
  const birthYear = birthStr.length === 8 ? parseInt(birthStr.slice(0, 4)) : parseInt('19' + birthStr.slice(0, 2));
  const birthMonth = parseInt(birthStr.slice(birthStr.length === 8 ? 4 : 2, birthStr.length === 8 ? 6 : 4));
  const year = parseInt(props.yearMonth.slice(0, 4));
  const month = parseInt(props.yearMonth.slice(4, 6));
  let age = year - birthYear;
  if (month < birthMonth) age--;
  return age;
});

const workPeriodText = computed(() => {
  if (!props.employee?.workStartDate || !props.yearMonth) return '-';
  const startStr = props.employee.workStartDate.replace(/[^0-9]/g, '');
  if (startStr.length < 6) return '-';
  const startYear = parseInt(startStr.slice(0, 4));
  const startMonth = parseInt(startStr.slice(4, 6));
  const year = parseInt(props.yearMonth.slice(0, 4));
  const month = parseInt(props.yearMonth.slice(4, 6));
  let years = year - startYear;
  let months = month - startMonth;
  if (months < 0) { years--; months += 12; }
  if (years > 0) return `${years}년 ${months}개월`;
  return `${months}개월`;
});

const severeCareDaily = computed(() => props.severeCareDaily || []);
const severeCareTotal = computed(() => severeCareDaily.value.reduce((sum, item) => sum + (parseFloat(item.suga_money) || 0), 0));
const hasFamilySuga = computed(() => props.employee?.patients?.some(p => p.sugaName?.includes('가족')));

const calendarServices = computed(() => {
  const services = props.services || [];
  const severeCareDaily = props.severeCareDaily || [];
  
  const normalServices = services.map((s: any) => ({
    date: s.service_date || s.date,
    patientName: s.patient_name || s.patientName,
    patientCode: s.patient_code || s.patientCode,
    sugaName: s.suga_name || s.sugaName,
    startTime: s.start_time || s.startTime,
    endTime: s.end_time || s.endTime,
    sugaMoney: s.suga_money || s.sugaMoney || 0,
    isFamily: (s.suga_name || s.sugaName)?.includes('가족') || false,
    isSevereCare: false
  }));
  
  const severeCareServices = severeCareDaily.map((s: any) => ({
    date: s.service_date || s.date,
    patientName: s.patient_name || s.patientName,
    patientCode: s.patient_code || s.patientCode,
    sugaName: s.suga_name || s.sugaName || '중증수급자 가산',
    startTime: s.start_time || s.startTime || '',
    endTime: s.end_time || s.endTime || '',
    sugaMoney: s.suga_money || s.sugaMoney || 0,
    isFamily: false,
    isSevereCare: true
  }));
  
  return [...normalServices, ...severeCareServices];
});

const isHoliday = (dateStr: string): boolean => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  if (date.getDay() === 0) return true;
  return !!props.holidays[dateStr];
};

const getSugaKey = (patient: Patient): string => {
  // StepThree.vue와 동일한 로직: 수가시간을 키에 포함하여 중복 카드 구분
  const hoursKey = patient.sugaHours ? `_h${patient.sugaHours}` : '';
  
  if (patient.sugaMinutesKey != null) {
    return `${patient.sugaCode}_${patient.sugaMinutesKey}${hoursKey}`;
  }
  return `${patient.sugaCode}${hoursKey}`;
};

// 270분초과 수가 여부 확인
const is270MinutesOverSuga = (patient: Patient): boolean => {
  return patient.sugaName?.includes('270분초과') || false;
};

// 환자별 수가시간 가져오기 (270분초과 수가는 30분 휴게시간 적용)
// 단, 8시간 이상 시에는 휴게시간 차감 없음
const getPatientSugaHours = (patient: Patient): number => {
  const baseHours = patient.sugaHours || 0;
  
  // 270분초과 수가인 경우 30분(0.5시간) 휴게시간 차감
  // 단, 8시간 이상 시에는 휴게시간 차감하지 않음
  if (is270MinutesOverSuga(patient)) {
    if (baseHours >= 8) {
      return baseHours; // 8시간 이상 시 휴게시간 차감 없음
    }
    return Math.max(0, baseHours - 0.5);
  }
  
  return baseHours;
};

// 수동 조정된 수가시간이 있으면 그 값을, 없으면 휴게시간 적용된 수가시간 반환
const getEffectiveSugaHours = (patient: Patient): number => {
  const sugaKey = getSugaKey(patient);
  const customHours = props.settings?.customSugaHours?.[patient.patientCode]?.[sugaKey];
  
  // 수동 조정된 값이 있으면 그대로 사용
  if (customHours !== undefined) {
    return customHours;
  }
  
  // 270분초과 수가인 경우 30분 휴게시간 적용
  return getPatientSugaHours(patient);
};

const getTimePay = (patient: Patient): string => props.settings?.timePays?.[patient.patientCode]?.[getSugaKey(patient)] || '';
const updateTimePay = (patient: Patient, value: string) => emit('update-timepay', props.groupIndex, patient.patientCode, getSugaKey(patient), value);
const updateSetting = (key: string, value: any) => emit('update-setting', props.groupIndex, key, value);

// 장기근속수당 입력 핸들러 - 값 입력 시 applyLongServicePay도 함께 업데이트
const handleLongServicePayInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value.replace(/[^\d]/g, ''); // 숫자만 추출
  if (value.length > 9) value = value.slice(0, 9);
  
  const numericValue = parseInt(value) || 0;
  // 입력 중에는 숫자만 저장 (포맷팅은 blur에서)
  emit('update-setting', props.groupIndex, 'longServicePay', value);
  emit('update-setting', props.groupIndex, 'applyLongServicePay', numericValue > 0);
};

// 장기근속수당 blur 핸들러 - 포맷팅 적용
const handleLongServicePayBlur = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value.replace(/[^\d]/g, '');
  const numericValue = parseInt(value) || 0;
  const formattedValue = numericValue > 0 ? new Intl.NumberFormat('ko-KR').format(numericValue) : '';
  emit('update-setting', props.groupIndex, 'longServicePay', formattedValue);
  emit('update-setting', props.groupIndex, 'applyLongServicePay', numericValue > 0);
};

// 기타수당 입력 핸들러 - 값 입력 시 applyEtcAllowance도 함께 업데이트
const handleEtcAllowanceInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value.replace(/[^\d]/g, '');
  if (value.length > 9) value = value.slice(0, 9);
  
  const numericValue = parseInt(value) || 0;
  emit('update-setting', props.groupIndex, 'etcAllowance', value);
  emit('update-setting', props.groupIndex, 'applyEtcAllowance', numericValue > 0);
};

// 기타수당 blur 핸들러 - 포맷팅 적용
const handleEtcAllowanceBlur = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value.replace(/[^\d]/g, '');
  const numericValue = parseInt(value) || 0;
  const formattedValue = numericValue > 0 ? new Intl.NumberFormat('ko-KR').format(numericValue) : '';
  emit('update-setting', props.groupIndex, 'etcAllowance', formattedValue);
  emit('update-setting', props.groupIndex, 'applyEtcAllowance', numericValue > 0);
};

// 보수교육비 입력 핸들러 - 값 입력 시 applyBosuEduPay도 함께 업데이트
const handleBosuEduPayInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value.replace(/[^\d]/g, ''); // 숫자만 추출
  if (value.length > 9) value = value.slice(0, 9);
  
  const numericValue = parseInt(value) || 0;
  // 입력 중에는 숫자만 저장 (포맷팅은 blur에서)
  emit('update-setting', props.groupIndex, 'bosuEduPay', value);
  emit('update-setting', props.groupIndex, 'applyBosuEduPay', numericValue > 0);
};

// 보수교육비 blur 핸들러 - 포맷팅 적용
const handleBosuEduPayBlur = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value.replace(/[^\d]/g, '');
  const numericValue = parseInt(value) || 0;
  const formattedValue = numericValue > 0 ? new Intl.NumberFormat('ko-KR').format(numericValue) : '';
  emit('update-setting', props.groupIndex, 'bosuEduPay', formattedValue);
  emit('update-setting', props.groupIndex, 'applyBosuEduPay', numericValue > 0);
};

// 기타공제 입력 핸들러 - 값 입력 시 applyEmpEtc도 함께 업데이트
const handleEmpEtcInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value.replace(/[^\d]/g, ''); // 숫자만 추출
  if (value.length > 9) value = value.slice(0, 9);
  
  const numericValue = parseInt(value) || 0;
  emit('update-setting', props.groupIndex, 'empEtc', value);
  emit('update-setting', props.groupIndex, 'applyEmpEtc', numericValue > 0);
};

// 기타공제 blur 핸들러 - 포맷팅 적용
const handleEmpEtcBlur = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value.replace(/[^\d]/g, '');
  const numericValue = parseInt(value) || 0;
  const formattedValue = numericValue > 0 ? new Intl.NumberFormat('ko-KR').format(numericValue) : '';
  emit('update-setting', props.groupIndex, 'empEtc', formattedValue);
  emit('update-setting', props.groupIndex, 'applyEmpEtc', numericValue > 0);
};

// 본인부담금 클릭 핸들러
const handleBoninPayClick = () => {
  if (props.isConfirmed) {
    store.commit('showSnackbar', { text: '마감된 급여는 수정할 수 없습니다.', color: 'warning' });
    return;
  }
  if (!hasFamilySuga.value) {
    store.commit('showSnackbar', { text: '본인부담금은 가족요양 서비스가 있는 경우에만 설정할 수 있습니다.', color: 'info' });
    return;
  }
  emit('open-bonin-modal', props.groupIndex);
};

const close = () => { dialogVisible.value = false; };
const formatMoney = (amount: number | string | undefined | null): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('ko-KR').format(Math.round(num || 0));
};
const formatServiceDate = (date: string | undefined): string => {
  if (!date) return '-';
  return date.substring(5).replace('-', '/');
};
const formatMinutes = (minutes: number): string => {
  if (!minutes) return '0분';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}시간 ${m}분`;
  if (h > 0) return `${h}시간`;
  return `${m}분`;
};

// 수가시간 표시 (커스텀 값이 있으면 커스텀 값, 없으면 휴게시간 적용된 값)
const getDisplaySugaHours = (patient: Patient): string => {
  const hours = getEffectiveSugaHours(patient);
  return hours.toFixed(1);
};

// 수가명이 공휴일가산인지 확인하는 함수
// '공휴일가산', '공휴일 가산', 또는 '공휴일'과 '가산'이 함께 포함된 경우
const isHolidaySuga = (sugaName: string | null | undefined): boolean => {
  if (!sugaName) return false;
  const name = sugaName.toLowerCase().replace(/\s/g, ''); // 공백 제거
  // '공휴일가산'이 포함되어 있거나, '공휴일'과 '가산'이 모두 포함된 경우
  if (name.includes('공휴일가산')) return true;
  if (name.includes('공휴일') && name.includes('가산')) return true;
  // '일요일가산'이 포함되어 있거나, '일요일'과 '가산'이 모두 포함된 경우
  if (name.includes('일요일가산')) return true;
  if (name.includes('일요일') && name.includes('가산')) return true;
  return false;
};

// 수급자별 휴일수당 계산 (총시간 × 최저시급 × 0.5)
const getPatientHolidayAllowance = (patient: Patient): number => {
  if (!isHolidaySuga(patient.sugaName)) return 0;
  
  const hours = getEffectiveSugaHours(patient);
  const serviceDays = patient.serviceDays || 0;
  const totalHours = serviceDays * hours;
  
  // 최저시급 사용 (급여정보 탭의 휴일수당 계산과 동일)
  const minWage = props.payrollDetail?.calcInfo?.minWage || 0;
  
  if (minWage === 0) return 0;
  
  return Math.round(totalHours * minWage * 0.5);
};

// 수급자별 기본금액 계산 (서비스횟수 × 수가시간 × 시급) - 휴일수당 미포함
const getPatientBaseAmount = (patient: Patient): number => {
  const hours = getEffectiveSugaHours(patient);
  const serviceDays = patient.serviceDays || 0;
  const timePay = getTimePay(patient);
  const timePayNum = timePay ? parseFloat(String(timePay).replace(/[^\d]/g, '')) || 0 : 0;
  
  // 시급이 입력되지 않은 경우 0 반환
  if (timePayNum === 0) return 0;
  
  return serviceDays * hours * timePayNum;
};

// 수급자별 합계금액 계산 (기본금액 + 휴일수당)
const getPatientTotalAmount = (patient: Patient): number => {
  const baseAmount = getPatientBaseAmount(patient);
  const holidayAllowance = props.settings?.applyHoliday ? getPatientHolidayAllowance(patient) : 0;
  return baseAmount + holidayAllowance;
};

// 전체 수급자 합계금액 (휴일수당 포함)
const getAllPatientsTotalAmount = (): number => {
  if (!props.employee?.patients) return 0;
  return props.employee.patients.reduce((sum, p) => sum + getPatientTotalAmount(p), 0);
};

// 전체 휴일수당 합계
const getTotalHolidayAllowance = (): number => {
  if (!props.employee?.patients) return 0;
  return props.employee.patients.reduce((sum, p) => sum + getPatientHolidayAllowance(p), 0);
};

const getPayrollTooltip = (field: string): string => {
  const payroll = props.payrollDetail;
  if (!payroll) return '';
  const fmt = (n: number) => new Intl.NumberFormat('ko-KR').format(Math.round(n || 0));
  const calcInfo = payroll.calcInfo || {};
  const minWage = calcInfo.minWage || 0;
  const totalSugaHours = calcInfo.totalSugaHours || 0;
  const annualLeaveDays = calcInfo.annualLeaveDays || 0;
  const workDaysForCalc = calcInfo.workDaysForCalc || 0;
  const totalMonthlyPay = calcInfo.totalMonthlyPay || 0;
  const baseVacationPay = calcInfo.baseVacationPay || 0;
  const remainingAmount = calcInfo.remainingAmount || 0;
  const vacationPayAddition = calcInfo.vacationPayAddition || 0;
  const yearLevel = calcInfo.yearLevel || 1;
  const totalMonthsWorked = calcInfo.totalMonthsWorked || 0;
  const mealAllowance = calcInfo.mealAllowance || 0;
  
  // 시급 역산 정보
  const wasWageAdjusted = calcInfo.wasWageAdjusted || false;
  const adjustedHourlyWage = calcInfo.adjustedHourlyWage || minWage;
  const adjustedBaseVacationPay = calcInfo.adjustedBaseVacationPay || 0;
  const vacationPayDifference = calcInfo.vacationPayDifference || 0;
  
  switch (field) {
    case 'basicPay': {
      if (wasWageAdjusted) {
        let tooltip = `[기본급 계산 - 시급 역산 적용]\n\n`;
        tooltip += `▶ 60시간 미만 시급 역산\n`;
        tooltip += `  합계금액: ${fmt(totalMonthlyPay)}원\n`;
        tooltip += `  수가시간: ${totalSugaHours.toFixed(1)}시간\n\n`;
        tooltip += `▶ 역산 시급 계산\n`;
        tooltip += `  이론적 최대 시급: ${fmt(totalMonthlyPay)}원 ÷ 1.2 ÷ ${totalSugaHours.toFixed(1)}시간\n`;
        tooltip += `  = ${fmt(Math.round(totalMonthlyPay / 1.2 / totalSugaHours))}원\n`;
        tooltip += `  → 10원 단위 내림: ${fmt(adjustedHourlyWage)}원\n\n`;
        tooltip += `▶ 기본급 계산\n`;
        tooltip += `  ${fmt(adjustedHourlyWage)}원 × ${totalSugaHours.toFixed(1)}시간 = ${fmt(payroll.basicPay || 0)}원\n\n`;
        tooltip += `※ 최저시급(${fmt(minWage)}원) 대비 +${fmt(adjustedHourlyWage - minWage)}원 조정`;
        return tooltip;
      }
      return `최저시급 × 수가시간 = ${fmt(minWage)}원 × ${totalSugaHours.toFixed(1)}시간 = ${fmt(payroll.basicPay || 0)}원`;
    }
    case 'holidayPay': {
      if (wasWageAdjusted) {
        let tooltip = `[주휴수당 계산 - 시급 역산 적용]\n\n`;
        tooltip += `▶ 조정된 기본급 기준\n`;
        tooltip += `  기본급: ${fmt(payroll.basicPay || 0)}원\n`;
        tooltip += `  주휴수당: ${fmt(payroll.basicPay || 0)}원 × 0.2 = ${fmt(payroll.holidayPay || 0)}원`;
        return tooltip;
      }
      return `기본급 × 0.2 = ${fmt(payroll.basicPay || 0)}원 × 0.2 = ${fmt(payroll.holidayPay || 0)}원`;
    }
    case 'vacationPay': {
      if (workDaysForCalc === 0) return '근무일수 정보 없음';
      
      const isUnder60Hours = calcInfo.isUnder60Hours || false;
      const wasVacationPayAdjusted = calcInfo.wasVacationPayAdjusted || false;
      const basicPlusHoliday = calcInfo.basicPlusHoliday || 0;
      
      let tooltip = `[연차수당 계산]\n\n`;
      
      // 60시간 미만 + 시급 역산 적용된 경우: 별도 처리
      if (isUnder60Hours && wasWageAdjusted) {
        tooltip += `━━━━━━━━━━━━━━━━━━━━\n`;
        tooltip += `⚠️ 월 60시간 미만 근무 (${totalSugaHours.toFixed(1)}시간)\n`;
        tooltip += `━━━━━━━━━━━━━━━━━━━━\n\n`;
        
        tooltip += `▶ 시급 역산 적용\n`;
        tooltip += `  수급자별 시급×수가시간 합계가 최저시급 기준\n`;
        tooltip += `  기본급+주휴수당+연차수당보다 높아 시급을 역산합니다.\n\n`;
        
        const vacationRatio = workDaysForCalc > 0 ? (annualLeaveDays / 12) / workDaysForCalc : 0;
        const totalRatio = 1 + 0.2 + vacationRatio;
        
        tooltip += `▶ 역산 시급 계산 (연차수당 포함)\n`;
        tooltip += `  합계금액: ${fmt(totalMonthlyPay)}원\n`;
        tooltip += `  비율: 1 + 0.2 + (${annualLeaveDays}일÷12÷${workDaysForCalc}일) = ${totalRatio.toFixed(4)}\n`;
        tooltip += `  이론적 시급: ${fmt(totalMonthlyPay)}원 ÷ ${totalSugaHours.toFixed(1)}h ÷ ${totalRatio.toFixed(4)}\n`;
        tooltip += `  → 10원 단위 내림: ${fmt(adjustedHourlyWage)}원\n\n`;
        
        tooltip += `▶ 역산 시급 기준 급여 (동일 시급 적용)\n`;
        tooltip += `  기본급: ${fmt(adjustedHourlyWage)}원 × ${totalSugaHours.toFixed(1)}h = ${fmt(payroll.basicPay || 0)}원\n`;
        tooltip += `  주휴수당: ${fmt(payroll.basicPay || 0)}원 × 0.2 = ${fmt(payroll.holidayPay || 0)}원\n`;
        tooltip += `  연차수당: (${fmt(adjustedHourlyWage)}원 × ${totalSugaHours.toFixed(1)}h ÷ ${workDaysForCalc}일) × ${annualLeaveDays}일 ÷ 12\n`;
        tooltip += `          = ${fmt(adjustedBaseVacationPay)}원\n\n`;
        
        tooltip += `▶ 합계 확인\n`;
        tooltip += `  기본급+주휴수당+연차수당: ${fmt(basicPlusHoliday + adjustedBaseVacationPay)}원\n`;
        tooltip += `  합계금액: ${fmt(totalMonthlyPay)}원\n`;
        
        if (vacationPayDifference > 0) {
          tooltip += `  → 차액: ${fmt(vacationPayDifference)}원 (연차수당에 추가)\n\n`;
          tooltip += `▶ 최종 연차수당\n`;
          tooltip += `  ${fmt(adjustedBaseVacationPay)}원 + ${fmt(vacationPayDifference)}원 = ${fmt(payroll.vacationPay || 0)}원`;
        } else {
          tooltip += `\n\n▶ 최종 연차수당: ${fmt(payroll.vacationPay || 0)}원`;
        }
        
        tooltip += `\n\n※ 60시간 미만 시 식대 미적용`;
        tooltip += `\n※ 최저시급(${fmt(minWage)}원) 대비 +${fmt(adjustedHourlyWage - minWage)}원 조정됨`;
      } else if (isUnder60Hours) {
        // 60시간 미만이지만 시급 역산 미적용
        tooltip += `━━━━━━━━━━━━━━━━━━━━\n`;
        tooltip += `⚠️ 월 60시간 미만 근무 (${totalSugaHours.toFixed(1)}시간)\n`;
        tooltip += `━━━━━━━━━━━━━━━━━━━━\n\n`;
        tooltip += `원칙적으로 60시간 미만 근무 시 연차수당은\n`;
        tooltip += `미지급이 원칙입니다.\n\n`;
        tooltip += `단, 수급자별 시급 및 수가시간으로 계산된\n`;
        tooltip += `합계금액(월급여)에 미달하는 경우,\n`;
        tooltip += `그 차액을 연차수당으로 지급합니다.\n\n`;
        
        // 연차일수 산정 기준
        tooltip += `▶ 연차일수 산정\n`;
        tooltip += `  근속기간: ${totalMonthsWorked}개월 → ${yearLevel}년차\n`;
        tooltip += `  연차일수: ${annualLeaveDays}일\n\n`;
        
        // 기본 연차수당 계산 (공식)
        tooltip += `▶ 기본 연차수당 (공식)\n`;
        tooltip += `  (최저시급 × 수가시간 ÷ 근무일수) × 연차일수 ÷ 12\n`;
        tooltip += `  = (${fmt(minWage)}원 × ${totalSugaHours.toFixed(1)}h ÷ ${workDaysForCalc}일) × ${annualLeaveDays}일 ÷ 12\n`;
        tooltip += `  = ${fmt(baseVacationPay)}원\n\n`;
        
        tooltip += `▶ 60시간 미만 조정\n`;
        tooltip += `  월급여(합계): ${fmt(totalMonthlyPay)}원\n`;
        tooltip += `  기본급+주휴수당: ${fmt(basicPlusHoliday)}원\n`;
        
        if (wasVacationPayAdjusted) {
          tooltip += `  → 기본급+주휴수당+연차수당(${fmt(baseVacationPay)}원)이\n`;
          tooltip += `     월급여를 초과하므로 연차수당 조정\n`;
          tooltip += `  → 조정된 연차수당: ${fmt(totalMonthlyPay)}원 - ${fmt(basicPlusHoliday)}원\n`;
          tooltip += `     = ${fmt(payroll.vacationPay || 0)}원\n\n`;
        } else if (basicPlusHoliday >= totalMonthlyPay) {
          tooltip += `  → 기본급+주휴수당이 이미 월급여 이상\n`;
          tooltip += `  → 연차수당: 0원\n\n`;
        } else {
          const diff = totalMonthlyPay - basicPlusHoliday;
          tooltip += `  → 여유금액: ${fmt(diff)}원\n`;
          tooltip += `  → 연차수당(${fmt(baseVacationPay)}원)이 여유금액 이내\n`;
          tooltip += `  → 연차수당 그대로 적용: ${fmt(payroll.vacationPay || 0)}원\n\n`;
        }
        
        tooltip += `▶ 최종 연차수당: ${fmt(payroll.vacationPay || 0)}원`;
        tooltip += `\n\n※ 60시간 미만 시 식대 미적용`;
      } else {
        // 60시간 이상: 기존 로직
        // 1. 연차일수 산정 기준
        tooltip += `▶ 연차일수 산정\n`;
        tooltip += `  근속기간: ${totalMonthsWorked}개월 → ${yearLevel}년차\n`;
        tooltip += `  연차일수: ${annualLeaveDays}일\n\n`;
        
        // 2. 기본 연차수당 계산 (공식)
        tooltip += `▶ 기본 연차수당 (공식)\n`;
        tooltip += `  (최저시급 × 수가시간 ÷ 근무일수) × 연차일수 ÷ 12\n`;
        tooltip += `  = (${fmt(minWage)}원 × ${totalSugaHours.toFixed(1)}h ÷ ${workDaysForCalc}일) × ${annualLeaveDays}일 ÷ 12\n`;
        tooltip += `  = ${fmt(baseVacationPay)}원\n\n`;
        
        // 3. 남은 금액 분배
        tooltip += `▶ 남은 금액 분배\n`;
        tooltip += `  월급여: ${fmt(totalMonthlyPay)}원\n`;
        tooltip += `  - 기본급: ${fmt(payroll.basicPay || 0)}원\n`;
        tooltip += `  - 주휴수당: ${fmt(payroll.holidayPay || 0)}원\n`;
        tooltip += `  - 기본 연차수당: ${fmt(baseVacationPay)}원\n`;
        tooltip += `  = 남은 금액: ${fmt(remainingAmount)}원\n\n`;
        
        // 4. 식대 적용 여부에 따른 처리
        if (mealAllowance > 0) {
          tooltip += `▶ 식대 차감 후 연차수당 가산\n`;
          tooltip += `  식대: ${fmt(mealAllowance)}원\n`;
          tooltip += `  연차수당 가산: ${fmt(vacationPayAddition)}원\n\n`;
        } else if (vacationPayAddition > 0) {
          tooltip += `▶ 남은 금액 전액 연차수당 가산\n`;
          tooltip += `  가산액: ${fmt(vacationPayAddition)}원\n\n`;
        }
        
        // 5. 최종 연차수당
        tooltip += `▶ 최종 연차수당\n`;
        tooltip += `  ${fmt(baseVacationPay)}원 + ${fmt(vacationPayAddition)}원 = ${fmt(payroll.vacationPay || 0)}원`;
      }
      
      return tooltip;
    }
    case 'familyBasicTotal':
      return '가족요양: 월급여 전체가 기본급으로 처리됩니다.';
    case 'mealAllowance':
      return '비과세 식대 (월 20만원 한도)';
    case 'longServicePay': {
      const longServicePayAmount = payroll.longServicePay || 0;
      if (longServicePayAmount === 0) {
        return '장기근속수당 미적용\n\n급여설정에서 장기근속수당을 입력하면 적용됩니다.';
      }
      
      let tooltip = '[장기근속수당]\n\n';
      tooltip += `▶ 설정 금액: ${fmt(longServicePayAmount)}원\n\n`;
      tooltip += `※ 급여설정에서 직접 입력한 금액입니다.\n`;
      tooltip += `※ 장기근속수당은 과세 대상입니다.`;
      
      return tooltip;
    }
    case 'etcAllowance': {
      const etcAllowanceAmount = payroll.etcAllowance || 0;
      if (etcAllowanceAmount === 0) {
        return '기타수당 미적용\n\n급여설정에서 기타수당을 입력하면 적용됩니다.';
      }
      
      let tooltip = '[기타수당]\n\n';
      tooltip += `▶ 설정 금액: ${fmt(etcAllowanceAmount)}원\n\n`;
      tooltip += `※ 급여설정에서 직접 입력한 금액입니다.\n`;
      tooltip += `※ 기타수당은 과세 대상입니다.`;
      
      return tooltip;
    }
    case 'bosuEduPay':
      return '보수교육 이수에 따른 수당';
    case 'severeCareAddition': {
      const dailyItems = props.severeCareDaily || [];
      if (dailyItems.length === 0) {
        return '중증수급자 가산 대상 없음';
      }
      
      let tooltip = '[중증수급자 가산 내역]\n\n';
      let total = 0;
      
      const patientGroups: Record<string, any[]> = {};
      dailyItems.forEach((item: any) => {
        const key = item.patient_name || '-';
        if (!patientGroups[key]) patientGroups[key] = [];
        patientGroups[key].push(item);
      });
      
      Object.entries(patientGroups).forEach(([patientName, items], groupIdx) => {
        let patientTotal = 0;
        tooltip += `▶ ${patientName}\n`;
        items.forEach((item: any) => {
          const date = item.service_date || '-';
          const dateStr = date.substring(5).replace('-', '/');
          const money = parseFloat(item.suga_money) || 0;
          patientTotal += money;
          tooltip += `   ${dateStr}: ${fmt(money)}원\n`;
        });
        total += patientTotal;
        tooltip += `   소계: ${fmt(patientTotal)}원 (${items.length}회)\n`;
        if (groupIdx < Object.keys(patientGroups).length - 1) tooltip += '\n';
      });
      
      tooltip += '\n────────────\n';
      tooltip += `합계: ${fmt(total)}원`;
      
      return tooltip;
    }
    case 'holidayAllowance': {
      if (!props.settings?.applyHoliday) {
        return '⚠️ 휴일수당 미적용 설정됨\n급여설정 탭에서 "휴일수당" 체크박스를 활성화하면 적용됩니다.';
      }
      const holidayMinutes = calcInfo.holidayMinutes || 0;
      const holidayHours = Math.round(holidayMinutes / 60 * 10) / 10;
      if ((payroll.holidayAllowance || 0) === 0) {
        return '휴일가산 수가 없음';
      }
      let tooltip = '[휴일수당 계산]\n\n';
      tooltip += `▶ 휴일가산 수가시간: ${holidayHours}시간 (${fmt(holidayMinutes)}분)\n`;
      tooltip += `▶ 최저시급: ${fmt(minWage)}원\n`;
      tooltip += `▶ 가산율: 0.5 (50%)\n\n`;
      tooltip += `────────────\n`;
      tooltip += `${holidayHours}시간 × ${fmt(minWage)}원 × 0.5 = ${fmt(payroll.holidayAllowance)}원\n\n`;
      tooltip += `※ 수가명에 "공휴일가산" 또는 "일요일가산"이 포함된 서비스만 대상`;
      return tooltip;
    }
    case 'overtimeAllowance': {
      if (!props.settings?.applyOvertime) {
        return '⚠️ 연장수당 미적용 설정됨\n급여설정 탭에서 "연장수당" 체크박스를 활성화하면 적용됩니다.';
      }
      const overtimeMinutes = calcInfo.overtimeMinutes || 0;
      const overtimeHours = Math.round(overtimeMinutes / 60 * 10) / 10;
      if ((payroll.overtimeAllowance || 0) === 0) {
        return '연장근무 없음';
      }
      let tooltip = '[연장수당 계산]\n\n';
      tooltip += `▶ 연장근무시간: ${overtimeHours}시간 (${fmt(overtimeMinutes)}분)\n`;
      tooltip += `▶ 최저시급: ${fmt(minWage)}원\n`;
      tooltip += `▶ 가산율: 0.5 (50%)\n\n`;
      tooltip += `────────────\n`;
      tooltip += `${overtimeHours}시간 × ${fmt(minWage)}원 × 0.5 = ${fmt(payroll.overtimeAllowance)}원\n\n`;
      tooltip += `※ 일 8시간 초과 근무 시간 기준`;
      return tooltip;
    }
    case 'totalPay':
      return `기본급 + 주휴수당 + 연차수당 + 기타수당 = ${fmt(payroll.totalPay || 0)}원`;
    case 'netPay':
      return `지급 합계(${fmt(payroll.totalPay || 0)}원) - 공제 합계(${fmt(payroll.totalEmployeeDeduction || 0)}원) = ${fmt(payroll.netPay || 0)}원`;
    default: 
      return '';
  }
};

const showAdminDeductionTooltip = computed(() =>
  shouldShowAdminDeductionTooltip(store.getters.isAdmin, store.getters.isImpersonating)
);

const deductionTooltip = (field: string): string => {
  if (!showAdminDeductionTooltip.value) return '';
  return buildDeductionTooltip(field, props.payrollDetail, props.settings);
};

const centerTooltip = (field: string): string => {
  if (!showAdminDeductionTooltip.value) return '';
  return buildCenterTooltip(field, props.payrollDetail, props.settings);
};

const getTaxTooltip = (field: string): string => {
  const payroll = props.payrollDetail;
  if (!payroll) return '';
  const fmt = (n: number) => new Intl.NumberFormat('ko-KR').format(Math.round(n || 0));
  
  switch (field) {
    case 'taxable':
      return `과세 대상 금액 (소득세 계산 기준) = ${fmt(payroll.taxableAmount || 0)}원`;
    case 'nonTaxable':
      return `비과세 금액 (식대 등) = ${fmt(payroll.nonTaxableAmount || 0)}원`;
    default:
      return '';
  }
};

const getProfitTooltip = (): string => {
  const payroll = props.payrollDetail;
  if (!payroll) return '';
  const fmt = (n: number) => new Intl.NumberFormat('ko-KR').format(Math.round(n || 0));
  const totalSuga = (props.employee?.totalSugaMoney || 0) + (payroll.severeCareAddition || 0);
  const totalPay = payroll.totalPay || 0;
  const centerDeduction = payroll.totalCenterDeduction || 0;
  
  return `총수가(${fmt(totalSuga)}원) - 직원지급액(${fmt(totalPay)}원) - 기관부담금(${fmt(centerDeduction)}원) = ${fmt(props.centerProfit || 0)}원`;
};

const getRetirementTooltip = (): string => {
  const fmt = (n: number) => new Intl.NumberFormat('ko-KR').format(Math.round(n));
  const details = props.retirementDetails || [];
  
  if (details.length === 0) {
    return '입사 이후 누적된 퇴직금 총액';
  }
  
  // 년월을 YYYY-MM 형식으로 변환
  const formatYm = (ym: string) => {
    if (ym.length === 6) {
      return `${ym.substring(0, 4)}-${ym.substring(4, 6)}`;
    }
    return ym;
  };
  
  let tooltip = '[월별 퇴직금 적립 내역]\n';
  let total = 0;
  
  details.forEach((item) => {
    const amount = Number(item.retirement_amount) || 0;
    total += amount;
    tooltip += `${formatYm(item.lemi_ym)}: ${fmt(amount)}원\n`;
  });
  
  tooltip += `────────────\n`;
  tooltip += `합계: ${fmt(total)}원`;
  
  return tooltip;
};

watch(dialogVisible, (visible) => { if (visible) activeTab.value = 'payroll'; });
</script>

<style lang="scss" scoped>
$border: #d4d4d4;
$bg: #f5f5f5;
$text: #333;
$text-sub: #666;
$primary: #2563eb;

.wage-modal {
  padding: 0 0 10px 0;
  background-color: #f5f5f5;
  border-radius: 4px !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  min-height: 90vh !important;
  max-height: 90vh !important;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid $border;

  .header-info {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .emp-selector-wrapper {
      display: flex;
      align-items: center;
      gap: 4px;
      
      .nav-btn {
        width: 28px;
        height: 28px;
        border: 1px solid $border;
        background: #fff;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        
        &:hover:not(:disabled) { background: $bg; }
        &:disabled { opacity: 0.4; cursor: default; }
        
        &.prev { border-radius: 4px 0 0 4px; border-right: none; }
        &.next { border-radius: 0 4px 4px 0; border-left: none; }
      }
      
      .emp-select {
        width: 140px;
        
        :deep(.v-field) {
          min-height: 28px !important;
          font-size: 14px;
          font-weight: 600;
          
          .v-field__input {
            padding: 2px 8px !important;
            min-height: 28px !important;
          }
          
          .v-field__append-inner {
            padding-top: 2px;
          }
        }
        
        :deep(.v-field--variant-outlined) {
          border-radius: 0;
        }
      }
    }
    
    .emp-count {
      font-size: 12px;
      font-weight: 500;
      color: $text-sub;
      padding: 4px 10px;
      background: $bg;
      border-radius: 4px;
      min-width: 50px;
      text-align: center;
    }
    
    .family-badge {
      font-size: 11px;
      font-weight: 500;
      color: #7c3aed;
      padding: 3px 8px;
      background: #ede9fe;
      border-radius: 4px;
    }
    
    .emp-meta { font-size: 12px; color: $text-sub; }
    
    .edit-start-date-btn {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px 8px;
      font-size: 11px;
      font-weight: 500;
      color: #6b7280;
      background: #f3f4f6;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.15s;
      
      &:hover {
        background: #e5e7eb;
        color: #374151;
        border-color: #d1d5db;
      }
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;

    .help-btn {
      width: 28px;
      height: 28px;
      border: 1px solid $border;
      background: #fff;
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #2563eb;
      
      &:hover { 
        background: #eff6ff; 
        border-color: #2563eb;
      }
    }

    .close-btn {
      width: 28px;
      height: 28px;
      border: none;
      background: none;
      font-size: 20px;
      color: $text-sub;
      cursor: pointer;
      &:hover { color: $text; }
    }
  }
}

// 도움말 모달
.help-dialog {
  .help-card {
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    border-radius: 12px !important;
    overflow: hidden;
  }
  
  .help-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: #2563eb;
    color: #fff;
    
    .help-title {
      font-size: 18px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .help-close {
      width: 32px;
      height: 32px;
      border: none;
      background: rgba(255,255,255,0.2);
      border-radius: 6px;
      color: #fff;
      font-size: 20px;
      cursor: pointer;
      
      &:hover { background: rgba(255,255,255,0.3); }
    }
  }
  
  .help-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
    background: #f8fafc;
  }
  
  .help-section {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    margin-bottom: 16px;
    overflow: hidden;
    
    &:last-child { margin-bottom: 0; }
    
    .section-title {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 14px 16px;
      background: #f1f5f9;
      border-bottom: 1px solid #e2e8f0;
      font-size: 15px;
      font-weight: 600;
      color: #1e293b;
      
      .section-icon {
        width: 28px;
        height: 28px;
        background: #2563eb;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
      }
    }
    
    .section-content {
      padding: 16px;
    }
    
    .help-step {
      display: flex;
      gap: 12px;
      padding: 12px 0;
      border-bottom: 1px dashed #e2e8f0;
      align-items: flex-start;
      
      &:last-child { border-bottom: none; padding-bottom: 0; }
      &:first-child { padding-top: 0; }
      
      &.highlight-step {
        background: #fef2f2;
        margin: 12px -16px -16px -16px;
        padding: 16px;
        border-bottom: none;
        border-radius: 0 0 10px 10px;
      }
      
      .step-num {
        width: 28px;
        height: 28px;
        min-width: 28px;
        background: #2563eb;
        color: #fff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 14px;
        
        &.important {
          background: #dc2626;
          font-size: 16px;
        }
      }
      
      .step-label {
        min-width: 110px;
        font-weight: 600;
        color: #334155;
        font-size: 14px;
        padding: 4px 10px;
        background: #f1f5f9;
        border-radius: 4px;
        text-align: center;
        
        &.blue {
          background: #dbeafe;
          color: #1d4ed8;
        }
        
        &.gray {
          background: #e5e7eb;
          color: #4b5563;
        }
        
        &.dark {
          background: #1f2937;
          color: #fff;
        }
      }
      
      .step-content {
        color: #475569;
        font-size: 15px;
        line-height: 1.7;
        flex: 1;
        
        &.important-text {
          color: #dc2626;
          font-weight: 500;
        }
        
        .step-tip {
          display: block;
          margin-top: 8px;
          padding: 8px 12px;
          background: #f0fdf4;
          border-left: 3px solid #22c55e;
          color: #166534;
          font-size: 13px;
          border-radius: 0 6px 6px 0;
        }
      }
    }
  }
  
  .help-footer {
    padding: 16px 20px;
    background: #fff;
    border-top: 1px solid #e2e8f0;
    text-align: center;
    
    .help-close-btn {
      padding: 12px 40px;
      background: #2563eb;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      
      &:hover {
        background: #1d4ed8;
      }
    }
  }
}

// 입사일 변경 모달
.start-date-dialog {
  .start-date-card {
    border-radius: 12px !important;
    overflow: hidden;
  }
  
  .start-date-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 18px;
    background: #374151;
    color: #fff;
    
    .start-date-title {
      font-size: 16px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .start-date-close {
      width: 28px;
      height: 28px;
      border: none;
      background: rgba(255,255,255,0.15);
      border-radius: 6px;
      color: #fff;
      font-size: 18px;
      cursor: pointer;
      
      &:hover { background: rgba(255,255,255,0.25); }
    }
  }
  
  .start-date-body {
    padding: 20px;
    
    .emp-info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      
      .label {
        font-size: 13px;
        color: #6b7280;
      }
      
      .value {
        font-size: 14px;
        font-weight: 500;
        color: #111827;
      }
    }
    
    .date-section {
      margin-top: 16px;
      padding: 14px;
      background: #f9fafb;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
      
      &:first-of-type {
        margin-top: 12px;
      }
      
      .date-section-title {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        font-weight: 600;
        color: #374151;
        margin-bottom: 10px;
        padding-bottom: 8px;
        border-bottom: 1px solid #e5e7eb;
      }
      
      .emp-info-row {
        border-bottom: none;
        padding: 6px 0;
      }
    }
    
    .date-input-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      
      .label {
        font-size: 13px;
        font-weight: 500;
        color: #374151;
      }
      
      .date-input {
        padding: 8px 12px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 14px;
        color: #111827;
        
        &:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
        }
        
        &:disabled {
          background: #f3f4f6;
          color: #9ca3af;
          cursor: not-allowed;
        }
      }
      
      .date-input-wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 8px;
        
        .end-date-toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #6b7280;
          cursor: pointer;
          
          input[type="checkbox"] {
            width: 14px;
            height: 14px;
            accent-color: #dc2626;
            cursor: pointer;
          }
          
          span {
            user-select: none;
          }
          
          &:hover {
            color: #374151;
          }
        }
      }
    }
    
    .warning-text {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 16px;
      padding: 10px 12px;
      background: #fffbeb;
      border: 1px solid #fef3c7;
      border-radius: 6px;
      font-size: 12px;
      color: #92400e;
    }
  }
  
  .start-date-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 18px;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
    
    .cancel-btn {
      padding: 8px 16px;
      background: #fff;
      color: #6b7280;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      
      &:hover {
        background: #f3f4f6;
      }
    }
    
    .save-btn {
      padding: 8px 20px;
      background: #2563eb;
      color: #fff;
      border: none;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      
      &:hover:not(:disabled) {
        background: #1d4ed8;
      }
      
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}

.tabs {
  display: flex;
  background: #fff;
  border-bottom: 1px solid $border;
  padding: 0 16px;

  button {
    padding: 10px 12px;
    border: none;
    background: none;
    font-size: 13px;
    color: $text-sub;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;

    &:hover { color: $text; }
    &.active { color: $primary; border-bottom-color: $primary; font-weight: 500; }
  }
}

.modal-body {
  padding: 16px;
  background: $bg;
  flex: 1;
  overflow-y: auto;
}

// 데이터 테이블
.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;
  border: 1px solid $border;

  &.full { margin-bottom: 12px; }

  th, td {
    padding: 8px 10px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }

  th {
    background: #fafafa;
    font-weight: 500;
    color: $text-sub;
    border-bottom-color: $border;
  }

  td {
    color: $text;
    &.num { text-align: right; font-variant-numeric: tabular-nums; }
    &.r { text-align: right; }
  }

  tbody tr:hover { background: #fafafa; }
  tbody tr:last-child td { border-bottom: none; }
  
  tbody tr.has-tooltip {
    cursor: help;
    
    &:hover {
      background: #f0f9ff;
    }
  }

  tfoot {
    td {
      font-weight: 600;
      background: #fafafa;
      border-top: 1px solid $border;
    }
  }

  .family { color: #7c3aed; }
  .over { color: #dc2626; font-weight: 500; }
  .week-cell { background: #fafafa; font-weight: 500; }
  .week-start td { border-top: 1px solid $border; }
}

.badge {
  display: inline-block;
  margin-left: 4px;
  padding: 1px 4px;
  font-size: 10px;
  font-weight: 500;
  border-radius: 2px;
  &.red { background: #fee2e2; color: #dc2626; }
  &.blue { background: #dbeafe; color: #2563eb; }
  &.warn { background: #fef3c7; color: #92400e; }
}

.text-muted {
  color: #9ca3af !important;
}

// 급여상세
.payroll-content {
  .payroll-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 12px;
  }

  .payroll-section {
    background: #fff;
    border: 1px solid $border;

    .section-head {
      padding: 8px 12px;
      font-size: 13px;
      font-weight: 600;
      background: #fafafa;
      border-bottom: 1px solid $border;
    }

    .data-table {
      border: none;
      th, td { padding: 6px 12px; }
    }
  }

  .net-pay-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    padding: 10px 12px;
    background: #f9fafb;
    border: 1px solid $border;
    margin-bottom: 12px;

    .calc { font-size: 12px; color: $text-sub; }
    .label { font-size: 13px; font-weight: 500; color: $text-sub; }
    .amount { font-size: 14px; font-weight: 600; color: $text; }
  }

  .info-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  .info-block {
    background: #fff;
    border: 1px solid $border;
    padding: 10px 12px;

    .block-title {
      font-size: 12px;
      font-weight: 600;
      color: $text-sub;
      margin-bottom: 8px;
      padding-bottom: 6px;
      border-bottom: 1px solid #eee;
    }

    .block-item {
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      padding: 3px 0;
      color: $text-sub;

      &.total { font-weight: 600; color: $text; margin-top: 4px; padding-top: 6px; border-top: 1px solid #eee; }
      &.profit { font-size: 14px; font-weight: 600; &.positive { color: #059669; } &.negative { color: #dc2626; } }
      
      &.has-tooltip {
        cursor: help;
        &:hover { background: #f0f9ff; border-radius: 2px; }
      }
    }
  }
}

// 실수령액 row 툴팁
.net-pay-row.has-tooltip {
  cursor: help;
  &:hover { background: #fef3c7; }
}

// 직원정보 (급여정보 탭 내 통합)
.employee-info-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid $border;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: $text;
    margin-bottom: 12px;
  }

  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .info-section {
    background: #fff;
    border: 1px solid $border;

    .section-head {
      padding: 8px 12px;
      font-size: 13px;
      font-weight: 600;
      background: #fafafa;
      border-bottom: 1px solid $border;
    }

    .info-list { padding: 8px 12px; }

    .info-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 5px 0;
      font-size: 13px;
      color: $text;

      &.warn { color: #b45309; }
      &.ok { color: #059669; }
    }
  }
}

// 서비스/주차별
.services-content, .weekly-content {
  .sub-section { margin-top: 16px; }
  .sub-title {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 8px;
    .sub-total { font-weight: 400; color: $text-sub; margin-left: 8px; }
  }
  
  // 정렬 가능한 테이블
  table.sortable {
    .sortable-th {
      cursor: pointer;
      user-select: none;
      white-space: nowrap;
      transition: background 0.15s;
      
      &:hover {
        background: #e2e8f0;
      }
      
      .sort-icon {
        font-size: 10px;
        margin-left: 4px;
        color: #94a3b8;
        
        &.active {
          color: #3b82f6;
        }
      }
    }
  }
}

.empty {
  padding: 40px;
  text-align: center;
  color: $text-sub;
  font-size: 13px;
  background: #fff;
  border: 1px solid $border;
}

// 설정
.settings-content {
  .settings-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 16px;
  }

  .settings-section {
    background: #fff;
    border: 1px solid $border;
    padding: 12px;

    .section-head {
      font-size: 13px;
      font-weight: 600;
      margin-bottom: 10px;
      padding-bottom: 8px;
      border-bottom: 1px solid #eee;
    }

    label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      padding: 4px 0;
      cursor: pointer;

      input[type="checkbox"] { width: 14px; height: 14px; }
      
      .inline-input {
        width: 80px;
        padding: 4px 6px;
        border: 1px solid $border;
        font-size: 12px;
        text-align: right;
        margin-left: 8px;
        &:disabled { background: #f5f5f5; }
      }
    }

    .checkbox-with-action {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .badge-inline {
        font-size: 10px;
        padding: 2px 6px;
        background: #fef3c7;
        color: #92400e;
        border-radius: 2px;
      }
    }

    .sub-input {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 4px 0 4px 20px;
      font-size: 12px;
      color: $text-sub;

      input {
        width: 100px;
        padding: 4px 6px;
        border: 1px solid $border;
        font-size: 12px;
        text-align: right;
        &:disabled { background: #f5f5f5; }
      }
    }

    .input-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 4px 0;
      font-size: 13px;

      input {
        width: 80px;
        padding: 4px 6px;
        border: 1px solid $border;
        font-size: 12px;
        text-align: right;
        &:disabled { background: #f5f5f5; }
        
        &.clickable {
          cursor: pointer;
          background: #f8fafc;
          &:hover { 
            background: #e2e8f0; 
            border-color: #94a3b8;
          }
        }
        
        &.not-applicable {
          cursor: help;
          background: #fafafa;
          color: #9ca3af;
          &::placeholder {
            color: #9ca3af;
            font-style: italic;
          }
        }
        
        &[readonly] {
          cursor: default;
          &.clickable { cursor: pointer; }
          &.not-applicable { cursor: help; }
        }
      }
    }
  }

  .timepay-section {
    background: #fff;
    border: 1px solid $border;
    padding: 12px;

    .section-head {
      font-size: 13px;
      font-weight: 600;
      margin-bottom: 10px;
    }

    .service-count {
      text-align: center;
      font-weight: 500;
      color: #374151;
    }

    .suga-hours-btn {
      padding: 4px 8px;
      border: 1px solid $border;
      background: #fff;
      font-size: 12px;
      cursor: pointer;
      &:hover { background: $bg; }
      &:disabled { opacity: 0.5; cursor: default; }
    }

    .timepay-input {
      width: 80px;
      padding: 4px 6px;
      border: 1px solid $border;
      font-size: 12px;
      text-align: right;
      &:disabled { background: #f5f5f5; }
    }

    .total-amount {
      font-weight: 500;
      color: #1e40af;
      white-space: nowrap;
    }

    .holiday-allowance-info {
      font-size: 11px;
      color: #059669;
      margin-top: 2px;
      
      .holiday-label {
        background: #d1fae5;
        color: #047857;
        padding: 1px 4px;
        border-radius: 2px;
        font-size: 10px;
        font-weight: 500;
      }
    }

    .holiday-badge {
      background: #fef3c7 !important;
      color: #92400e !important;
      font-size: 10px;
      padding: 1px 4px;
      border-radius: 2px;
      margin-left: 4px;
      font-weight: 500;
    }

    td.holiday {
      background: #fffbeb;
    }

    tfoot td {
      font-weight: 600;
      background: #f8fafc;
      border-top: 1px solid $border;
    }
  }

  .settings-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid $border;

    .action-btn {
      padding: 8px 16px;
      font-size: 13px;
      font-weight: 500;
      border: 1px solid $border;
      cursor: pointer;
      transition: all 0.15s ease;

      &:disabled {
        opacity: 0.5;
        cursor: default;
      }

      &.load-prev {
        background: #fff;
        color: #2563eb;
        border-color: #2563eb;
        margin-right: auto;

        &:hover:not(:disabled) {
          background: #eff6ff;
        }
      }

      &.reset {
        background: #fff;
        color: $text-sub;

        &:hover:not(:disabled) {
          background: $bg;
        }
      }

      &.save {
        background: $text !important;
        color: #fff !important;
        border-color: $text !important;

        &:hover:not(:disabled) {
          background: #1f2937 !important;
          color: #fff !important;
        }

        &.unsaved {
          background: #dc2626 !important;
          border-color: #dc2626 !important;

          &:hover:not(:disabled) {
            background: #b91c1c !important;
            color: #fff !important;
          }
        }
      }
    }
  }
}

.family { color: #7c3aed; }

// 확정 상태 알림 (모달 내부용)
.confirmed-notice-modal {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  margin: 0 16px 8px 16px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
  border-radius: 8px;
  
  .notice-text {
    flex: 1;
    font-size: 0.82rem;
    color: #92400e;
    line-height: 1.4;
    
    strong {
      color: #78350f;
      font-weight: 700;
    }
  }
}
</style>
